import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { formatAmount } from "@/utils/formatAmount";
import type { CustomPricePayload } from "../api";
import { useCustomPriceMutations, useCustomPrices } from "../hooks";
import type { CustomPriceEntry } from "../types";

const KIND_LABELS: Record<CustomPriceEntry["kind"], string> = {
  work_unit: "Pozycja główna",
  material: "Materiał (M)",
  equipment: "Sprzęt (S)",
};

/** „Pozycja główna” to cała usługa/robota wyceniana kompleksowo (nadzór archeologiczny,
 *  obsługa geodezyjna) — wstawiasz ją do kosztorysu ręcznie. Materiał/sprzęt to
 *  pojedynczy nakład, który dopasowuje się sam przy każdej wycenie. */
const KIND_HINTS: Record<CustomPriceEntry["kind"], string> = {
  work_unit:
    "Cała usługa/robota z ceną kompleksową — wstawiasz ją do kosztorysu przyciskiem „Dodaj pozycję” (nie dopasowuje się automatycznie).",
  material: "Pojedynczy nakład materiałowy — dopasowuje się automatycznie przy wycenach.",
  equipment: "Pojedynczy nakład sprzętowy — dopasowuje się automatycznie przy wycenach.",
};

const NAME_PLACEHOLDERS: Record<CustomPriceEntry["kind"], string> = {
  work_unit: "np. Nadzór archeologiczny nad robotami ziemnymi",
  material: "np. Opaska kablowa typu Oki",
  equipment: "np. Samochód dostawczy typu bus do 0,9 t",
};

function money(v: string | null): string {
  if (v == null || v === "") return "—";
  const n = Number(v);
  return Number.isNaN(n) ? v : formatAmount(n);
}

interface FormState {
  kind: CustomPriceEntry["kind"];
  description: string;
  unit: string;
  price_avg: string;
  price_min: string;
  price_max: string;
  note: string;
  source_url: string;
}

const EMPTY_FORM: FormState = {
  kind: "material",
  description: "",
  unit: "",
  price_avg: "",
  price_min: "",
  price_max: "",
  note: "",
  source_url: "",
};

function toPayload(f: FormState): CustomPricePayload {
  const num = (s: string) => {
    const t = s.trim().replace(",", ".").replace(/\s/g, "");
    return t ? t : null;
  };
  return {
    kind: f.kind,
    description: f.description.trim(),
    unit: f.unit.trim(),
    price_avg: num(f.price_avg),
    price_min: num(f.price_min),
    price_max: num(f.price_max),
    note: f.note.trim(),
    source_url: f.source_url.trim(),
  };
}

function PriceForm({
  initial,
  onSubmit,
  onCancel,
  pending,
  submitLabel,
}: {
  initial: FormState;
  onSubmit: (f: FormState) => void;
  onCancel?: () => void;
  pending?: boolean;
  submitLabel: string;
}) {
  const [f, setF] = useState<FormState>(initial);
  const set = (k: keyof FormState) => (v: string) => setF((s) => ({ ...s, [k]: v }));
  const inputCls =
    "border border-[var(--color-border)] bg-[var(--color-bg-surface)] text-sm px-2 py-1 text-[var(--color-text-primary)]";
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs text-[var(--color-text-secondary)] flex flex-col gap-1">
          Rodzaj
          <select
            value={f.kind}
            onChange={(e) => set("kind")(e.target.value)}
            className={inputCls}
          >
            <option value="work_unit">Pozycja główna (usługa/robota)</option>
            <option value="material">Materiał (M)</option>
            <option value="equipment">Sprzęt (S)</option>
          </select>
        </label>
        <label className="flex-1 min-w-[240px] text-xs text-[var(--color-text-secondary)] flex flex-col gap-1">
          Nazwa pozycji
          <input value={f.description} onChange={(e) => set("description")(e.target.value)} className={inputCls} placeholder={NAME_PLACEHOLDERS[f.kind]} />
        </label>
        <label className="w-20 text-xs text-[var(--color-text-secondary)] flex flex-col gap-1">
          Jm
          <input value={f.unit} onChange={(e) => set("unit")(e.target.value)} className={inputCls} placeholder="m-g" />
        </label>
        <label className="w-28 text-xs text-[var(--color-text-secondary)] flex flex-col gap-1">
          Cena śr. [zł]
          <input value={f.price_avg} onChange={(e) => set("price_avg")(e.target.value)} className={inputCls} />
        </label>
        <label className="w-28 text-xs text-[var(--color-text-secondary)] flex flex-col gap-1">
          Cena min [zł]
          <input value={f.price_min} onChange={(e) => set("price_min")(e.target.value)} className={inputCls} />
        </label>
        <label className="w-28 text-xs text-[var(--color-text-secondary)] flex flex-col gap-1">
          Cena maks [zł]
          <input value={f.price_max} onChange={(e) => set("price_max")(e.target.value)} className={inputCls} />
        </label>
      </div>
      <p className="text-[11px] text-[var(--color-text-muted)]">{KIND_HINTS[f.kind]}</p>
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex-1 min-w-[240px] text-xs text-[var(--color-text-secondary)] flex flex-col gap-1">
          Notatka (skąd cena, na co uważać)
          <input value={f.note} onChange={(e) => set("note")(e.target.value)} className={inputCls} />
        </label>
        <label className="flex-1 min-w-[240px] text-xs text-[var(--color-text-secondary)] flex flex-col gap-1">
          Źródło (URL)
          <input value={f.source_url} onChange={(e) => set("source_url")(e.target.value)} className={inputCls} placeholder="https://…" />
        </label>
        <Button
          onClick={() => onSubmit(f)}
          disabled={!f.description.trim() || !f.price_avg.trim() || pending}
          loading={pending}
        >
          {submitLabel}
        </Button>
        {onCancel && (
          <button className="text-xs text-[var(--color-text-secondary)] underline pb-2" onClick={onCancel}>
            Anuluj
          </button>
        )}
      </div>
    </div>
  );
}

/** „Nasza baza cen” — własne, ręcznie zweryfikowane pozycje cenowe. Wpisy biorą
 *  udział w automatycznym dopasowaniu przy każdej wycenie (obok cennika
 *  SEKOCENBUD) i można je wybrać ręcznie w pickerze „zmień/dopasuj”. */
export function CustomPricesPage() {
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  const entries = useCustomPrices(q, kind);
  const { create, update, remove } = useCustomPriceMutations();
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const items = entries.data ?? [];

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-[var(--color-text-primary)]">Nasza baza cen</h1>
        <p className="text-sm text-[var(--color-text-secondary)] mt-1">
          Nietypowe pozycje, których nie ma w cenniku SEKOCENBUD, wycenione przez nas
          ręcznie (np. po własnym researchu w necie). Materiały i sprzęt są automatycznie
          uwzględniane przy dopasowaniu nakładów w każdej kolejnej wycenie — nie trzeba
          szukać drugi raz. <strong>Pozycje główne</strong> (nadzór archeologiczny, obsługa
          geodezyjna, organizacja ruchu…) to całe usługi wyceniane kompleksowo — wstawia się
          je do kosztorysu z listy przyciskiem „Dodaj pozycję”. Ceny można tu w każdej chwili
          skorygować.
        </p>
      </div>

      <div className="card p-4 space-y-3">
        {adding ? (
          <PriceForm
            initial={EMPTY_FORM}
            submitLabel="Dodaj do bazy"
            pending={create.isPending}
            onCancel={() => setAdding(false)}
            onSubmit={(f) =>
              create.mutate(toPayload(f), { onSuccess: () => setAdding(false) })
            }
          />
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={() => setAdding(true)}>+ Dodaj pozycję</Button>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Szukaj po nazwie lub kodzie…"
              className="flex-1 min-w-[220px] border border-[var(--color-border)] bg-[var(--color-bg-surface)] text-sm px-2 py-1"
            />
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              className="border border-[var(--color-border)] bg-[var(--color-bg-surface)] text-sm px-2 py-1"
            >
              <option value="">wszystkie rodzaje</option>
              <option value="work_unit">Pozycje główne</option>
              <option value="material">Materiały (M)</option>
              <option value="equipment">Sprzęt (S)</option>
            </select>
          </div>
        )}
        {create.isError && (
          <p className="text-xs text-red-500">
            Nie udało się zapisać pozycji — sprawdź nazwę i cenę średnią.
          </p>
        )}
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] text-[var(--color-text-muted)] border-b border-[var(--color-border)]">
              <th className="px-3 py-2 font-medium">Kod</th>
              <th className="px-3 py-2 font-medium">Rodzaj</th>
              <th className="px-3 py-2 font-medium">Nazwa</th>
              <th className="px-3 py-2 font-medium">Jm</th>
              <th className="px-3 py-2 font-medium text-right">Cena min</th>
              <th className="px-3 py-2 font-medium text-right">Cena śr.</th>
              <th className="px-3 py-2 font-medium text-right">Cena maks</th>
              <th className="px-3 py-2 font-medium">Notatka / źródło</th>
              <th className="px-3 py-2 font-medium">Dodał(a)</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--color-border)]">
            {entries.isLoading && (
              <tr>
                <td colSpan={10} className="px-3 py-3 text-[var(--color-text-muted)]">
                  Wczytywanie…
                </td>
              </tr>
            )}
            {!entries.isLoading && items.length === 0 && (
              <tr>
                <td colSpan={10} className="px-3 py-3 text-[var(--color-text-muted)]">
                  {q || kind
                    ? "Brak pozycji dla tego filtra."
                    : "Baza jest pusta — dodaj pierwszą pozycję powyżej albo przyciskiem „dodaj do naszej bazy” przy nakładzie w wynikach wyceny."}
                </td>
              </tr>
            )}
            {items.map((e) =>
              editingId === e.id ? (
                <tr key={e.id}>
                  <td colSpan={10} className="px-3 py-3 bg-[var(--color-bg-elevated)]">
                    <PriceForm
                      initial={{
                        kind: e.kind,
                        description: e.description,
                        unit: e.unit,
                        price_avg: e.price_avg ?? "",
                        price_min: e.price_min ?? "",
                        price_max: e.price_max ?? "",
                        note: e.note,
                        source_url: e.source_url,
                      }}
                      submitLabel="Zapisz zmiany"
                      pending={update.isPending}
                      onCancel={() => setEditingId(null)}
                      onSubmit={(f) =>
                        update.mutate(
                          { id: e.id, payload: toPayload(f) },
                          { onSuccess: () => setEditingId(null) },
                        )
                      }
                    />
                  </td>
                </tr>
              ) : (
                <tr key={e.id} className="hover:bg-[var(--color-bg-base)]">
                  <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{e.code}</td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">{KIND_LABELS[e.kind]}</td>
                  <td className="px-3 py-2">{e.description}</td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">{e.unit || "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{money(e.price_min)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium whitespace-nowrap">
                    {money(e.price_avg)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{money(e.price_max)}</td>
                  <td className="px-3 py-2 text-xs text-[var(--color-text-secondary)] max-w-[240px]">
                    {e.note && <div className="truncate" title={e.note}>{e.note}</div>}
                    {e.source_url && (
                      <a
                        href={e.source_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[var(--color-primary)] hover:underline break-all"
                      >
                        źródło
                      </a>
                    )}
                    {!e.note && !e.source_url && "—"}
                  </td>
                  <td className="px-3 py-2 text-xs text-[var(--color-text-muted)] whitespace-nowrap">
                    {e.created_by_name}
                    {e.created_at && (
                      <> · {new Date(e.created_at).toLocaleDateString("pl-PL")}</>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button
                      className="text-[11px] text-[var(--color-primary)] hover:underline"
                      onClick={() => setEditingId(e.id)}
                    >
                      edytuj
                    </button>
                    <button
                      className="ml-2 text-[11px] text-red-600 hover:underline"
                      onClick={() => {
                        if (window.confirm("Usunąć tę pozycję z naszej bazy?"))
                          remove.mutate(e.id);
                      }}
                    >
                      usuń
                    </button>
                  </td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
